import validator from 'validator'
import { apiService } from '../../../api/config'

let mode = 'python'
let get_mode = 'python'

/** Benefit codes that are allowed to have quantity > 1 per plan. All others are limited to 1. */
export const BENEFIT_CODES_ALLOW_MULTIPLE_QUANTITY = ['D0230', 'D0240'];

export function canBenefitHaveMultipleQuantity(benefit) {
    return benefit && BENEFIT_CODES_ALLOW_MULTIPLE_QUANTITY.includes(benefit.code);
}

// Component mapping system to replace hardcoded numbers
// Simple, clean lowercase names for easy navigation (case-insensitive)
export const COMPONENT_NAMES = {
    // Main onboarding flow
    pageone: 0,
    ucrinfo: 1,
    adult: 2,
    child: 3,
    perio: 4,
    addons: 5,
    planpricediscounts: 6,
    enrollment: 7,
    rewards: 8,
    contact: 9,
    overview: 10,
    loading: 11,
    
    // Intake flow components
    stepone: 0,
    intake_contact: 1,
    intake_ucrs: 2,
    intake_confirm: 3
}

// Helper function to get component number by name
export function GetComponentNumber(componentName) {
    const lowerName = componentName.toLowerCase();
    
    if (COMPONENT_NAMES[lowerName] !== undefined) {
        return COMPONENT_NAMES[lowerName];
    }
    
    console.warn(`Component name "${componentName}" not found. Available names:`, Object.keys(COMPONENT_NAMES));
    return null;
}

// Enhanced GoToComponent function that accepts component names
export function GoToComponent(instance, componentIdentifier) {
    let componentNumber;
    
    if (typeof componentIdentifier === 'string') {
        componentNumber = GetComponentNumber(componentIdentifier);
        if (componentNumber === null) {
            console.error(`Invalid component name: ${componentIdentifier}`);
            return;
        }
    } else if (typeof componentIdentifier === 'number') {
        componentNumber = componentIdentifier;
    } else {
        console.error('GoToComponent expects a string (component name) or number (component index)');
        return;
    }
    
    //console.log("Emitting from GoToComponent");
    instance.$emit('switchComponent', { number: componentNumber });
}

// Helper function for NavigateToComponent calls
export function NavigateToComponentByName(componentName) {
    const componentNumber = GetComponentNumber(componentName);
    if (componentNumber === null) {
        console.error(`Invalid component name: ${componentName}`);
        return null;
    }
    return { number: componentNumber };
}

export function InputToInteger(str) {
    str = str.replace(/\D/g, '');
    return +str;
}

export function FormatCurrency(value) {
    // Convert to number if it's a string
    const num = typeof value === 'string' ? parseFloat(value) : value;
    
    // Check if it's a valid number
    if (isNaN(num)) return '0';
    
    // If it's a whole number, return as integer
    if (num % 1 === 0) {
        return num.toString();
    }
    
    // Otherwise, format to 2 decimal places
    return num.toFixed(2);
}

export function ConvertInts(plan) {
    plan.rewardsPointsPerVisit = InputToInteger(String(plan.rewardsPointsPerVisit));
    plan.reimbursementPercentage = InputToInteger(String(plan.reimbursementPercentage));
    plan.enrollmentFee = InputToInteger(String(plan.enrollmentFee));
    plan.dentalMenuFee = parseFloat(String(plan.dentalMenuFee));
}

export async function SendPlanInformation(plan, isIntake = false) {
    if(plan.practiceName === null) {
        return;
    }
    ConvertInts(plan);
    return apiService.addPlan(plan, isIntake);
}

export function GetPlanFromDB(planKey) {
    return apiService.getPlan(planKey);
}

export async function UpdatePlanDataInDB(inputId, plan, changelog) {
    let newPlan = JSON.parse(JSON.stringify(plan));
    delete newPlan.id;
    delete newPlan.allStates;
    delete newPlan.uneditedState;
    ConvertInts(plan);
    newPlan.benefits.forEach((benefit) => {
        if (benefit.payout === undefined || benefit.payout === null) {
            benefit.payout = (benefit.price !== undefined && benefit.price !== null) ? benefit.price : 0;
        }
    });
    return apiService.updatePlan(inputId, newPlan, changelog);
}

export async function SubmitPlanToDB(planKey, extraData) {
    return apiService.submitPlan(planKey, extraData);
}

export async function CreateLandingPageUtil(planKey, extraData) {
    return apiService.createLandingPage(planKey, extraData);
}

export function AddRewardsCostToPrice(store, plan, basePrice) {
    return basePrice + ((store.state.rewardsPointsPerVisit / plan.frequency) * (store.state.reimbursementPercentage / 100))
}

export function AddDMFeeToPrice(store, basePrice) {
    return basePrice + store.state.dentalMenuFee;
}

export function AddProcessingFeeToPrice(basePrice) {
    return (basePrice * 1.029) + 0.3;
}

export function CalculateComputedPlanPrice(store, plan) {
    //console.log("Entering calculate price")
    if(plan.monthlyPriceAltered) {
        return plan.price;
    }
    let basePrice = 0;
    const planType = plan.planType;
    // console.log("planType", planType);
    store.getters.benefitsByType(planType).forEach((benefit) => {
        // console.log("benefit name:", benefit.name);
        // console.log("benefit.frequencies:", benefit.frequencies);
        const benefitFrequency = benefit.frequencies[planType] === -1 ? plan.frequency : benefit.frequencies[planType];
        // console.log("benefitFrequency:", benefitFrequency);
        // console.log("Benefit price:", benefit.price);
        const pricePerUnitPerMonth = benefit.price / (benefitFrequency * (1 + (store.state.percentBreakage[planType] / 100)));
        // console.log("pricePerUnitPerMonth:", pricePerUnitPerMonth);
        const rawQuantity = store.getters.getBenefitQuantityForPlanType(benefit, planType) || 1;
        // console.log("rawQuantity:", rawQuantity);
        const quantity = canBenefitHaveMultipleQuantity(benefit) ? rawQuantity : 1;
        // console.log("quantity:", quantity);
        basePrice += pricePerUnitPerMonth * quantity;
        // console.log("basePrice after calculating for benefit ", benefit.name, " = ", basePrice);
    });
    let rewardsAdjustedPrice = basePrice;
    if(store.state.includePointsInPlanPrice) {
        rewardsAdjustedPrice = AddRewardsCostToPrice(store, plan, basePrice);
    }
    // console.log("rewardsAdjustedPrice", rewardsAdjustedPrice);
    let merchantFeeAdjustedPrice = store.state.includeProcessingFee ? AddProcessingFeeToPrice(rewardsAdjustedPrice) : rewardsAdjustedPrice;
    // console.log("merchantFeeAdjustedPrice", merchantFeeAdjustedPrice);
    let DMFeeAdjustedPrice = store.state.includeDMFee ? AddDMFeeToPrice(store, merchantFeeAdjustedPrice) : merchantFeeAdjustedPrice;
    // console.log("DMFeeAdjustedPrice", DMFeeAdjustedPrice);
    let roundedPrice = Math.round(DMFeeAdjustedPrice);
    return roundedPrice
}

export async function IsLandingPageAvailable(landingPageURL) {
    try {
        const result = await apiService.checkLandingPageUrl(landingPageURL);
        return result.isAvailable;
    } catch (error) {
        console.error("Error checking landing page:", error);
        throw error;
    }
}

export function ValidateWebsite(website) {
    if(website === "") {
        return true;
    }
    return validator.isURL(website);
}

export function ValidateEmail(email) {
    if(email === "") {
        return true;
    }
    return validator.isEmail(email);
}

export async function CheckLandingPageURL(landingPage) {
    if (landingPage.length === 0) {
        return { isAvailable: false, error: null };
    }
    return apiService.checkLandingPageUrl(landingPage);
}

export function ValidateBenefitCode(code) {
    if (!code) return false;
    const codes = code.split(',').map(code => code.trim());
    const regex = /^D\d{4}$/;
    return codes.every(code => regex.test(code));
}